"use client";

import React, { useEffect, useState, useMemo } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { cn } from "@/lib/utils";

// Dynamically import only motion.div from 'motion/react'
const DynamicMotionDiv = dynamic(
  async () => {
    const mod = await import("motion/react");
    return mod.motion.div;
  },
  { ssr: false }
);

// We'll display all of these images in the carousel
const proofImages = [
  // Original batches
  {
    id: 1,
    src: "/images/batch-proof/122Batch.jpg",
    alt: "BatchFinder user earnings - $122 batch accepted automatically"
  },
  {
    id: 2,
    src: "/images/batch-proof/54Batch.jpeg",
    alt: "BatchFinder user earnings - $54 batch accepted automatically"
  },
  {
    id: 3,
    src: "/images/batch-proof/40Batch.jpeg",
    alt: "BatchFinder user earnings - $40 batch accepted automatically"
  },
  {
    id: 4,
    src: "/images/batch-proof/averageDay.jpg",
    alt: "BatchFinder user average daily earnings screenshot"
  },
  // Notification screenshots
  {
    id: 5,
    src: "/images/batch-proof/notifs1.png",
    alt: "BatchFinder notification - Batch auto-accepted notification"
  },
  {
    id: 6,
    src: "/images/batch-proof/notifs2.png",
    alt: "BatchFinder notification - High paying batch accepted"
  },
  {
    id: 7,
    src: "/images/batch-proof/notifs3.png",
    alt: "BatchFinder notification - Multi-customer batch accepted"
  },
  {
    id: 8,
    src: "/images/batch-proof/notifs4.png",
    alt: "BatchFinder notification - High value batch notification"
  },
  {
    id: 9,
    src: "/images/batch-proof/notifs5.png",
    alt: "BatchFinder notification - Batch accepted successfully"
  },
  // Shopper screenshots
  {
    id: 10,
    src: "/images/batch-proof/shopper2.png",
    alt: "Instacart shopper earnings with BatchFinder assistance"
  },
  {
    id: 11,
    src: "/images/batch-proof/shopper3.png",
    alt: "Instacart shopper weekly earnings with BatchFinder"
  },
  {
    id: 12,
    src: "/images/batch-proof/shopper4.png",
    alt: "Instacart shopper high batch earnings with BatchFinder"
  },
  {
    id: 13,
    src: "/images/batch-proof/shopper5.png",
    alt: "Instacart shopper daily earnings with BatchFinder"
  }
];

export function BatchProof() {
  return (
    <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-screen-xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Real Earnings from Real Users
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            These are actual screenshots shared by our customers. See what you could be earning with BatchFinder.
          </p>
        </div>
        
        <div className="flex justify-center">
          <CardStack images={proofImages} />
        </div>
        
        <div className="mt-12 text-center">
          <p className="font-semibold text-primary text-xl mb-2">Ready to start earning more?</p>
          <p className="text-gray-600 max-w-xl mx-auto mb-6">
            Join thousands of shoppers who are using BatchFinder to get the best batches before anyone else.
          </p>
        </div>
      </div>
    </div>
  );
}

// Card stack component
let interval: any;

export const CardStack = ({
  images,
  offset = 10,
  scaleFactor = 0.06,
  className = "",
}: {
  images: Array<{id: number; src: string; alt: string}>;
  offset?: number;
  scaleFactor?: number;
  className?: string;
}) => {
  const CARD_OFFSET = offset;
  const SCALE_FACTOR = scaleFactor;
  const MAX_VISIBLE_CARDS = 5; // Limit visible cards to 5
  
  // We don't need to convert the images - they're already in the right format
  const initialCards = useMemo(() => images, [images]);
  
  const [cards, setCards] = useState(initialCards);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRotating, setIsRotating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      // First rotate the front card
      setIsRotating(true);
      
      // After the rotation, start the transition
      setTimeout(() => {
        setIsRotating(false);
        setIsTransitioning(true);
        
        // Then update the index which will change the card order
        setTimeout(() => {
          setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
          
          // After a smooth transition delay, reset the transition state
          setTimeout(() => {
            setIsTransitioning(false);
          }, 800);
        }, 300);
      }, 600); // Longer delay for the rotation animation
      
    }, 6000); // Increased total time between transitions
    
    return () => clearInterval(timer);
  }, [images.length]);

  // This effect updates the cards array when currentIndex changes
  useEffect(() => {
    if (!isTransitioning) {
      const rotated = [
        ...initialCards.slice(currentIndex),
        ...initialCards.slice(0, currentIndex)
      ];
      setCards(rotated);
    }
  }, [currentIndex, initialCards, isTransitioning]);

  return (
    <div className={cn("relative w-72 h-[500px] sm:w-80 sm:h-[550px] md:w-96 md:h-[600px]", className)}>
      {cards.slice(0, MAX_VISIBLE_CARDS).map((card, index) => {
        const cardScale = 1 - index * SCALE_FACTOR;
        
        // Determine the appropriate animation state based on the card position and transition state
        const getAnimationState = () => {
          // Front card animation
          if (index === 0) {
            if (isRotating) {
              return {
                rotate: [-1, 1, 0],
                y: [0, -8, -4],
                scale: cardScale
              };
            } else if (isTransitioning) {
              return {
                y: [-4, -20],
                rotate: [0, -2],
                scale: [cardScale, cardScale * 0.97]
              };
            }
            return {
              rotate: 0,
              y: 0,
              scale: cardScale
            };
          }
          
          // Second card animation - grow slightly during transition
          if (index === 1 && isTransitioning) {
            const targetScale = 1 - (index - 1) * SCALE_FACTOR; // Scale as if it were the front card
            return {
              rotate: 0,
              y: [0, -5, 0], // Slight upward movement as it comes forward
              scale: [cardScale, targetScale * 1.02, targetScale] // Grow slightly larger than target then settle
            };
          }
          
          // Default animation for other cards
          return {
            rotate: 0,
            y: 0,
            scale: cardScale
          };
        };
        
        const animationState = getAnimationState();
        
        return (
          <DynamicMotionDiv
            key={card.id}
            className="absolute bg-white rounded-xl overflow-hidden shadow-xl border border-neutral-200 dark:border-white/[0.1]"
            style={{
              width: "100%",
              height: "100%",
              transformOrigin: "top center",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
            }}
            animate={{
              top: index * -CARD_OFFSET,
              scale: animationState.scale,
              zIndex: MAX_VISIBLE_CARDS - index,
              opacity: 1,
              rotate: animationState.rotate,
              y: animationState.y
            }}
            transition={{
              type: isTransitioning ? "tween" : "spring",
              stiffness: 180,
              damping: 30, 
              mass: 1,
              duration: isTransitioning ? 0.8 : undefined,
              ease: isTransitioning ? "easeInOut" : undefined,
              rotate: {
                duration: 0.6,
                ease: "easeInOut"
              },
              y: {
                duration: isTransitioning ? 0.8 : 0.6,
                ease: "easeInOut"
              },
              scale: {
                duration: isTransitioning ? 0.8 : 0.6,
                ease: index === 1 ? "easeOut" : "easeInOut" // Special easing for second card
              }
            }}
          >
            <div className="relative w-full h-full">
              <Image
                src={card.src}
                alt={card.alt}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 80vw, 400px"
                priority={index === 0}
                loading={index < 2 ? "eager" : "lazy"}
              />
            </div>
          </DynamicMotionDiv>
        );
      })}
    </div>
  );
}; 